import { Link } from "react-router-dom";
import {
  Download, Palette, FileText, ScrollText, Book, ArrowRight
} from "lucide-react";
import ExecutiveHero from "@/components/ExecutiveHero";
import { OFFICIAL_SEAL } from "@/lib/brand";

const COLORS = [
  { name: "Zodiac Navy",       hex: "#0F1E47", role: "Primary brand" },
  { name: "Institutional Blue", hex: "#1E3A8A", role: "Action / link" },
  { name: "Authority Red",     hex: "#991B1B", role: "Accent / editorial" },
  { name: "Heritage Gold",     hex: "#C8A157", role: "Highlight / seal" },
  { name: "Warm Ivory",        hex: "#FBFAF7", role: "Page surface" },
  { name: "Hairline",          hex: "#E5E7EB", role: "Borders / dividers" },
];

const ASSETS = [
  { icon: ScrollText, title: "Founder Biography",         desc: "Long-form professional bio",         status: "Available Soon" },
  { icon: FileText,   title: "Institutional Fact Sheet",  desc: "One-page institutional overview",    status: "Available Soon" },
  { icon: Book,       title: "WAIT Theory Executive Summary", desc: "3-page doctrinal summary",       status: "Available Soon" },
  { icon: ScrollText, title: "SCFR Executive Summary",    desc: "Journal scope, board, and editorial program", status: "Available Soon" },
];

export default function MediaKit() {
  return (
    <div className="min-h-screen zodiac-page" data-testid="media-kit-page">
      <ExecutiveHero
        eyebrow="Brand & Media Kit"
        title={<>Institutional brand</>}
        accentTitle="resources."
        subtitle="Official seal, brand colors, usage guidelines, and downloadable institutional documents — for journalists, partners, designers, and academic collaborators."
        image="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=2400&q=80"
        breadcrumb={[{ label: "Home", href: "/" }, { label: "Press", href: "/press" }, { label: "Media Kit" }]}
        actions={[
          { label: "Press Center", href: "/press", testId: "media-kit-press-cta" },
          { label: "Contact", href: "/contact", variant: "ghost" },
        ]}
      />

      {/* Official Logo + Seal */}
      <section className="bg-white py-20" data-testid="media-kit-seal">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#C8A157]" /> Official Seal & Logo
          </div>
          <h2 className="zodiac-heading text-3xl font-bold text-[#0F1E47] mb-10">The institutional mark.</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="zodiac-hairline-card p-10 flex flex-col items-center text-center">
              <img
                src={OFFICIAL_SEAL}
                alt="Zodiac Workforce Intelligence Institute Seal"
                className="w-48 h-48 object-contain drop-shadow-[0_12px_30px_rgba(15,30,71,0.18)] mb-6"
              />
              <h3 className="zodiac-heading text-lg font-bold text-[#0F1E47]">Official Seal (Color)</h3>
              <p className="text-sm text-[#6B7280] mb-5">PNG · Transparent background · 4K</p>
              <a href={OFFICIAL_SEAL} download="zodiac-institute-seal.png">
                <button className="h-10 px-4 rounded-md bg-[#0F1E47] text-white hover:bg-[#1E3A8A] font-semibold inline-flex items-center gap-2 text-sm" data-testid="download-seal">
                  <Download className="w-4 h-4" /> Download Seal
                </button>
              </a>
            </div>

            <div className="zodiac-hairline-card p-10 flex flex-col items-center text-center bg-[#0F1E47]">
              <img
                src={OFFICIAL_SEAL}
                alt="Zodiac Workforce Intelligence Institute Seal — on dark"
                className="w-48 h-48 object-contain drop-shadow-[0_12px_30px_rgba(200,161,87,0.4)] mb-6"
              />
              <h3 className="zodiac-heading text-lg font-bold text-white">Seal on Dark Surface</h3>
              <p className="text-sm text-white/60 mb-5">Use over navy / deep brand backgrounds</p>
              <a href={OFFICIAL_SEAL} download="zodiac-institute-seal.png">
                <button className="h-10 px-4 rounded-md bg-[#C8A157] text-[#0F1E47] hover:bg-[#E5C275] font-semibold inline-flex items-center gap-2 text-sm">
                  <Download className="w-4 h-4" /> Download Seal
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Colors */}
      <section className="bg-[#FBFAF7] border-y border-[#E5E7EB] py-20" data-testid="media-kit-colors">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#1E3A8A]" /> Brand Colors
          </div>
          <h2 className="zodiac-heading text-3xl font-bold text-[#0F1E47] mb-3">The institutional palette.</h2>
          <p className="text-[#475569] mb-10 max-w-2xl">
            Use these colors with consistent contrast and hierarchy. Navy is primary;
            gold is reserved for editorial highlight; red is reserved for authority accents.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {COLORS.map((c) => (
              <div key={c.hex} className="rounded-xl overflow-hidden border border-[#E5E7EB] bg-white">
                <div className="h-24" style={{ backgroundColor: c.hex }} />
                <div className="p-4">
                  <p className="font-semibold text-[#0F1E47]">{c.name}</p>
                  <p className="font-mono text-sm text-[#6B7280]">{c.hex}</p>
                  <p className="text-xs text-[#9CA3AF] mt-1 uppercase tracking-widest">{c.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Usage Guidelines */}
      <section className="bg-white py-20" data-testid="media-kit-usage">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#991B1B]" /> Brand Usage Guidelines
          </div>
          <h2 className="zodiac-heading text-3xl font-bold text-[#0F1E47] mb-8 leading-tight">
            Use the institutional brand with <span className="italic text-[#991B1B] font-normal">executive restraint.</span>
          </h2>
          <ul className="space-y-3 text-[#475569]">
            {[
              "Maintain ample whitespace around the seal — minimum clear-space equal to the seal's inner diameter.",
              "Do not recolor, distort, rotate, or apply effects to the official seal.",
              "Reproduce on solid or light surfaces; the dark-surface variant is provided above.",
              "Use Playfair Display for institutional headings; Inter for body text.",
              "Capitalize 'Zodiac Workforce Intelligence Institute' on first reference; 'ZWII' is acceptable thereafter.",
              "Capitalize 'WAIT Theory' and 'SCFR' as proper nouns.",
              "Do not imply endorsement of partners, employers, or government agencies without written agreement.",
            ].map((g, i) => (
              <li key={i} className="flex items-start gap-3 p-4 rounded-lg border border-[#E5E7EB] bg-[#FBFAF7]">
                <span className="w-5 h-5 rounded-full bg-[#C8A157]/20 text-[#7B5A1A] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Downloadable Documents */}
      <section className="bg-[#FBFAF7] border-y border-[#E5E7EB] py-20" data-testid="media-kit-documents">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#0F766E]" /> Institutional Documents
          </div>
          <h2 className="zodiac-heading text-3xl font-bold text-[#0F1E47] mb-3">Executive resources.</h2>
          <p className="text-[#475569] mb-10 max-w-2xl">
            Curated institutional documents for journalists, partners, and academic collaborators.
            New resources released as publications are finalized.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {ASSETS.map((a) => (
              <div key={a.title} className="zodiac-hairline-card p-6 flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#0F766E]/10 flex items-center justify-center flex-shrink-0">
                  <a.icon className="w-5 h-5 text-[#0F766E]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="zodiac-heading text-base font-bold text-[#0F1E47]">{a.title}</h3>
                  <p className="text-sm text-[#6B7280] mt-1">{a.desc}</p>
                  <div className="mt-3 inline-flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-[#9CA3AF] bg-[#F4F6FB] px-2.5 py-1 rounded-full">
                      {a.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-[#0F1E47] text-white py-20 relative overflow-hidden">
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#C8A157]/10 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Palette className="w-10 h-10 text-[#C8A157] mx-auto mb-5" />
          <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold mb-4 leading-tight">
            Need a specific <span className="italic text-[#C8A157] font-normal">asset?</span>
          </h2>
          <p className="text-white/70 mb-8 leading-relaxed">
            Press, academic, and partner inquiries are welcome. The Institute will provide
            additional formats or co-branded materials on request.
          </p>
          <Link to="/contact">
            <button className="h-12 px-6 rounded-md bg-[#C8A157] text-[#0F1E47] hover:bg-[#E5C275] font-semibold inline-flex items-center gap-2">
              Request Brand Assets <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
